import Axios from 'axios';
import { SelectBox } from 'devextreme-react';
import { Column, DataGrid, FilterRow, HeaderFilter, Paging, Scrolling } from 'devextreme-react/data-grid';
import Cookies from 'js-cookie';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    FormGroup,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Table
} from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import { downloadSampleExcel } from '../../components/common/excel-upload/excel-upload';
import { goRiceUrl } from '../../constant/siteConstants';
import api from '../../services/api-configuration';
import { getAllWarehouses } from '../../services/apis';
import useFormatMessage from '../../utils/useFormatMessage';

const AntrepoTransferEmri = () => {
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(22);

    // Form states
    const [file, setFile] = useState(null);
    const [sourceWarehouse, setSourceWarehouse] = useState(null);
    const [targetWarehouse, setTargetWarehouse] = useState(null);
    const [sourceWarehouseList, setSourceWarehouseList] = useState([]);
    const [bondedWarehouseList, setBondedWarehouseList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Optional customs documents (T1 & IM7) for new order
    const [t1Number, setT1Number] = useState('');
    const [t1Date, setT1Date] = useState('');
    const [t1File, setT1File] = useState(null);
    const [im7Number, setIm7Number] = useState('');
    const [im7Date, setIm7Date] = useState('');
    const [im7File, setIm7File] = useState(null);
    const [notes, setNotes] = useState('');

    // Transfer orders list
    const [transferList, setTransferList] = useState([]);
    const [isLoadingList, setIsLoadingList] = useState(false);

    // Edit documents modal
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editT1Number, setEditT1Number] = useState('');
    const [editT1Date, setEditT1Date] = useState('');
    const [editT1File, setEditT1File] = useState(null);
    const [editIm7Number, setEditIm7Number] = useState('');
    const [editIm7Date, setEditIm7Date] = useState('');
    const [editIm7File, setEditIm7File] = useState(null);
    const [editNotes, setEditNotes] = useState('');
    const [isSavingDocs, setIsSavingDocs] = useState(false);

    // Chassis list modal
    const [isChassisModalOpen, setIsChassisModalOpen] = useState(false);
    const [activeChassisList, setActiveChassisList] = useState([]);

    const fileInputRef = useRef(null);

    useEffect(() => {
        loadWarehouses();
        loadTransferList();
    }, []);

    const loadWarehouses = async () => {
        try {
            const list = await getAllWarehouses(true);
            setSourceWarehouseList(list || []);

            let bondedList = [];
            try {
                const bondedRes = await api.post('/bonded-transfer/warehouses');
                if (bondedRes.data && bondedRes.data.data && Array.isArray(bondedRes.data.data)) {
                    bondedList = bondedRes.data.data;
                }
            } catch (bErr) {
                console.error('Error fetching bonded warehouses:', bErr);
            }

            // Fallback if endpoint returns empty: filter list for warehouses with antrepo zone
            if (bondedList.length === 0 && Array.isArray(list)) {
                bondedList = list.filter(w => w.warehouse_name && (
                    w.warehouse_name.toUpperCase().includes('ANTREPO') ||
                    w.warehouse_name.toUpperCase().includes('SAMANDIRA')
                ));
            }
            setBondedWarehouseList(bondedList);

            // Default selections: exit port or first warehouse, and first bonded warehouse
            if (Array.isArray(list) && list.length > 0) {
                const liman = list.find(w => w.warehouse_name && w.warehouse_name.toUpperCase().includes('LİMAN'));
                setSourceWarehouse(liman ? liman.id : list[0].id);
            }
            if (bondedList.length > 0) {
                setTargetWarehouse(bondedList[0].id);
            }
        } catch (err) {
            console.error('Error fetching warehouses:', err);
        }
    };

    const loadTransferList = async () => {
        setIsLoadingList(true);
        try {
            const res = await api.post('/bonded-transfer/list');
            if (res.data && res.data.data) {
                setTransferList(res.data.data);
            }
        } catch (err) {
            console.error('Error fetching bonded transfer list:', err);
        } finally {
            setIsLoadingList(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const clearFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const uploadCustomsFile = async (docFile) => {
        if (!docFile) return { file_name: '', file_path: '' };
        try {
            const formData = new FormData();
            formData.append('file', docFile);
            const res = await api.post('/bonded-transfer/upload-document', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return {
                file_name: res.data?.file_name || docFile.name,
                file_path: res.data?.file_path || ''
            };
        } catch (err) {
            console.warn('Customs file upload error:', err);
            return { file_name: docFile.name, file_path: '' };
        }
    };

    const handleCreateOrder = async () => {
        if (!file) {
            toast.error(t('Lütfen taşınacak araçların Excel dosyasını seçiniz.'));
            return;
        }
        if (!sourceWarehouse || !targetWarehouse) {
            toast.error(t('Lütfen çıkış ve varış depolarını seçiniz.'));
            return;
        }
        if (sourceWarehouse === targetWarehouse) {
            toast.error(t('Çıkış deposu ve varış deposu aynı olamaz!'));
            return;
        }

        // Validate that target warehouse has a defined antrepo zone
        const isTargetBonded = bondedWarehouseList.some(w => w.id === targetWarehouse);
        if (bondedWarehouseList.length > 0 && !isTargetBonded) {
            toast.error(t('Bu depoda antrepo bölgesi yok! Lütfen antrepo tanımlı bir varış deposu seçiniz.'));
            return;
        }

        setIsSubmitting(true);
        try {
            const headers = {
                'Content-Type': 'multipart/form-data',
                token: Cookies.get('token')
            };

            const empIdStr = localStorage.getItem('employee_id');
            const userId = empIdStr ? parseInt(Buffer.from(empIdStr, 'base64').toString('ascii')) : 1;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('transfer_confirmation', '1');
            formData.append('user_id', userId);
            formData.append('process_type', 'WAREHOUSE_TRANSFER');
            formData.append('is_bonded', '1');
            formData.append('transfer_type', 'ANTREPO');
            formData.append('target_warehouse_id', targetWarehouse);
            formData.append('located_warehouse_id', sourceWarehouse);

            const response = await Axios.post(`${goRiceUrl}/api/vehicle/add`, formData, { headers });

            if (response.data && response.data.success) {
                toast.success(t('Antrepo transfer emri başarıyla oluşturuldu.'));

                // Upload optional T1 / IM7 documents if provided
                if (t1Number || im7Number || t1File || im7File) {
                    let uploadedT1 = { file_name: '', file_path: '' };
                    let uploadedIm7 = { file_name: '', file_path: '' };

                    if (t1File) uploadedT1 = await uploadCustomsFile(t1File);
                    if (im7File) uploadedIm7 = await uploadCustomsFile(im7File);

                    // Fetch latest order list to get newly created order ID
                    await loadTransferList();
                    const latestListRes = await api.post('/bonded-transfer/list');
                    const latestList = latestListRes.data?.data || [];
                    const newOrderId = latestList.length > 0 ? latestList[0].order_id : null;

                    if (newOrderId) {
                        await api.post('/bonded-transfer/save-documents', {
                            order_id: newOrderId,
                            from_warehouse_id: sourceWarehouse,
                            to_warehouse_id: targetWarehouse,
                            t1_number: t1Number,
                            t1_date: t1Date || null,
                            t1_file_name: uploadedT1.file_name,
                            t1_file_path: uploadedT1.file_path,
                            im7_number: im7Number,
                            im7_date: im7Date || null,
                            im7_file_name: uploadedIm7.file_name,
                            im7_file_path: uploadedIm7.file_path,
                            notes: notes
                        });
                        toast.success(t('Gümrük evrakları (T1 / IM7) emre başarıyla bağlandı.'));
                    }
                }

                // Reset inputs
                clearFile();
                setT1Number('');
                setT1Date('');
                setT1File(null);
                setIm7Number('');
                setIm7Date('');
                setIm7File(null);
                setNotes('');
                await loadTransferList();
            } else {
                toast.error(t('Dosya yüklenirken hata oluştu.'));
            }
        } catch (error) {
            console.error('Order creation error:', error);
            const errMsg = error.response?.data?.error || t('Transfer emri oluşturulurken bir hata oluştu.');
            toast.error(errMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const openDocModal = (order) => {
        setSelectedOrder(order);
        setEditT1Number(order.t1_number || '');
        setEditT1Date(order.t1_date ? order.t1_date.substring(0, 10) : '');
        setEditT1File(null);
        setEditIm7Number(order.im7_number || '');
        setEditIm7Date(order.im7_date ? order.im7_date.substring(0, 10) : '');
        setEditIm7File(null);
        setEditNotes(order.notes || '');
        setIsDocModalOpen(true);
    };

    const handleSaveDocumentsModal = async () => {
        if (!selectedOrder) return;
        setIsSavingDocs(true);
        try {
            let uploadedT1 = {
                file_name: selectedOrder.t1_file_name || '',
                file_path: selectedOrder.t1_file_path || ''
            };
            let uploadedIm7 = {
                file_name: selectedOrder.im7_file_name || '',
                file_path: selectedOrder.im7_file_path || ''
            };

            if (editT1File) uploadedT1 = await uploadCustomsFile(editT1File);
            if (editIm7File) uploadedIm7 = await uploadCustomsFile(editIm7File);

            await api.post('/bonded-transfer/save-documents', {
                order_id: selectedOrder.order_id,
                from_warehouse_id: selectedOrder.from_warehouse_id,
                to_warehouse_id: selectedOrder.to_warehouse_id,
                t1_number: editT1Number,
                t1_date: editT1Date || null,
                t1_file_name: uploadedT1.file_name,
                t1_file_path: uploadedT1.file_path,
                im7_number: editIm7Number,
                im7_date: editIm7Date || null,
                im7_file_name: uploadedIm7.file_name,
                im7_file_path: uploadedIm7.file_path,
                notes: editNotes
            });

            toast.success(t('Gümrük evrakları başarıyla güncellendi.'));
            setIsDocModalOpen(false);
            await loadTransferList();
        } catch (err) {
            console.error('Error saving documents:', err);
            toast.error(t('Evraklar kaydedilirken hata oluştu.'));
        } finally {
            setIsSavingDocs(false);
        }
    };

    const openChassisModal = (order) => {
        setSelectedOrder(order);
        setActiveChassisList(order.chassis_list || []);
        setIsChassisModalOpen(true);
    };

    return (
        <>
            <Breadcrumb current={t("Antrepo Transfer Emri")} />

            {/* Top Info Banner */}
            <Alert color="info" className="mb-3">
                <i className="fa fa-info-circle mr-2"></i>
                <strong>{t("Gümrüklü Antrepo Transfer Süreci:")}</strong>{" "}
                {t("Gümrüklü araçların liman veya antrepolar arası sevkinde gümrük evraklarının (T1 Dahili Transit ve IM7 Antrepo Beyannamesi) sisteme girilmesi tamamen ")}
                <strong>{t("opsiyoneldir")}</strong>.{" "}
                {t("Belgelerin henüz hazır olmaması araçların sevkine ve transfer emrinin açılmasına engel değildir. Belgeleri emir açılırken girebileceğiniz gibi, sonradan da dilediğiniz an ilgili emre ekleyebilir veya güncelleyebilirsiniz.")}
            </Alert>

            {/* Order Creation Card */}
            <Card className="mb-4">
                <CardHeader className="bg-primary text-white py-2">
                    <h5 className="mb-0"><i className="fa fa-truck mr-2"></i>{t("Yeni Antrepo Transfer Emri Oluştur")}</h5>
                </CardHeader>
                <CardBody>
                    <Row className="p-2">
                        <Col md="6">
                            <FormGroup>
                                <Label><b>{t("Çıkış Sahası / Liman")}</b> <span className="text-danger">*</span></Label>
                                <SelectBox
                                    id="source_warehouse_id"
                                    dataSource={sourceWarehouseList}
                                    valueExpr="id"
                                    displayExpr="warehouse_name"
                                    value={sourceWarehouse}
                                    onValueChanged={(e) => setSourceWarehouse(e.value)}
                                    placeholder={t("Çıkış sahası veya liman seçiniz")}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label><b>{t("Varış Antreposu")}</b> <span className="text-danger">*</span></Label>
                                <SelectBox
                                    id="target_warehouse_id"
                                    dataSource={bondedWarehouseList}
                                    valueExpr="id"
                                    displayExpr="warehouse_name"
                                    value={targetWarehouse}
                                    onValueChanged={(e) => setTargetWarehouse(e.value)}
                                    placeholder={t("Varış antreposu seçiniz")}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className="p-2">
                        <Col md="12">
                            <FormGroup>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <Label className="mb-0"><b>{t("Taşınacak Araçlar (Excel)")}</b> <span className="text-danger">*</span></Label>
                                    <span
                                        onClick={() => downloadSampleExcel(["sase"], "", "Antrepo Transfer")}
                                        style={{ color: "blue", cursor: "pointer", fontSize: "0.85rem" }}
                                    >
                                        <i className="fa fa-download mr-1"></i>{t("Örnek Excel Şablonunu İndir")}
                                    </span>
                                </div>
                                <InputGroup>
                                    <Input
                                        id="excelUpload"
                                        type="file"
                                        accept=".xlsx, .xls"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        innerRef={fileInputRef}
                                    />
                                    <Input
                                        type="text"
                                        placeholder={t("Taşınacak araçlar listesini içeren Excel dosyasını seçiniz...")}
                                        value={file ? file.name : ''}
                                        readOnly
                                    />
                                    <InputGroupAddon addonType="append">
                                        <InputGroupText
                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            style={{ cursor: 'pointer', backgroundColor: '#e9ecef' }}
                                        >
                                            <i className="fa fa-folder-open mr-1"></i>{t("Dosya Seç")}
                                        </InputGroupText>
                                        {file && (
                                            <InputGroupText onClick={clearFile} style={{ cursor: 'pointer', color: 'red' }}>
                                                <i className="fa fa-times"></i>
                                            </InputGroupText>
                                        )}
                                    </InputGroupAddon>
                                </InputGroup>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Optional Customs Documents Section */}
                    <div className="border rounded p-3 mb-3 bg-light">
                        <h6 className="text-secondary mb-3">
                            <i className="fa fa-file-text-o mr-2"></i>
                            <strong>{t("Gümrük Evrakları")}</strong> <Badge color="secondary">{t("Opsiyonel")}</Badge>
                        </h6>
                        <Row>
                            {/* T1 Document Section */}
                            <Col md="6" className="border-right">
                                <h7 className="font-weight-bold text-dark d-block mb-2">
                                    <i className="fa fa-shield text-info mr-1"></i>{t("T1 Dahili Transit Belgesi (NCTS)")}
                                </h7>
                                <FormGroup>
                                    <Label>{t("T1 Belge / MRN No")}</Label>
                                    <Input
                                        id="input-t1-number"
                                        type="text"
                                        placeholder="Örn: 26TR34000012345"
                                        value={t1Number}
                                        onChange={(e) => setT1Number(e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t("T1 Belge Tarihi")}</Label>
                                    <Input
                                        id="input-t1-date"
                                        type="date"
                                        value={t1Date}
                                        onChange={(e) => setT1Date(e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t("T1 Belgesi Dosyası (PDF / Görsel)")}</Label>
                                    <Input
                                        id="input-t1-file"
                                        type="file"
                                        accept=".pdf, .png, .jpg, .jpeg"
                                        onChange={(e) => setT1File(e.target.files[0] || null)}
                                    />
                                    {t1File && <small className="text-success"><i className="fa fa-check mr-1"></i>{t1File.name}</small>}
                                </FormGroup>
                            </Col>

                            {/* IM7 Document Section */}
                            <Col md="6">
                                <h7 className="font-weight-bold text-dark d-block mb-2">
                                    <i className="fa fa-building text-warning mr-1"></i>{t("IM7 Antrepo Beyannamesi (Fiktif Giriş)")}
                                </h7>
                                <FormGroup>
                                    <Label>{t("IM7 Beyanname No")}</Label>
                                    <Input
                                        id="input-im7-number"
                                        type="text"
                                        placeholder="Örn: 26340700IM000456"
                                        value={im7Number}
                                        onChange={(e) => setIm7Number(e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t("IM7 Tescil Tarihi")}</Label>
                                    <Input
                                        id="input-im7-date"
                                        type="date"
                                        value={im7Date}
                                        onChange={(e) => setIm7Date(e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t("IM7 Beyanname Dosyası (PDF / Görsel)")}</Label>
                                    <Input
                                        id="input-im7-file"
                                        type="file"
                                        accept=".pdf, .png, .jpg, .jpeg"
                                        onChange={(e) => setIm7File(e.target.files[0] || null)}
                                    />
                                    {im7File && <small className="text-success"><i className="fa fa-check mr-1"></i>{im7File.name}</small>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row className="mt-2">
                            <Col md="12">
                                <FormGroup className="mb-0">
                                    <Label>{t("Operasyonel Notlar / Açıklama")}</Label>
                                    <Input
                                        id="input-bonded-notes"
                                        type="textarea"
                                        rows="2"
                                        placeholder={t("Varsa transfer ve gümrük süreciyle ilgili notlarınızı giriniz...")}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>

                    <div className="d-flex justify-content-end">
                        <Button
                            id="btn-create-bonded-transfer"
                            color="success"
                            disabled={isSubmitting || !file || !sourceWarehouse || !targetWarehouse}
                            onClick={handleCreateOrder}
                            className="px-4"
                        >
                            <i className={`fa ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-check'} mr-2`}></i>
                            {t("Transfer Emrini Oluştur")}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* Transfer List and Documents Management Card */}
            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center py-2">
                    <h5 className="mb-0"><i className="fa fa-list mr-2"></i>{t("Antrepo Transferleri ve Gümrük Evrakları Takip Listesi")}</h5>
                    <Button color="secondary" size="sm" onClick={loadTransferList} disabled={isLoadingList}>
                        <i className={`fa ${isLoadingList ? 'fa-spinner fa-spin' : 'fa-refresh'} mr-1`}></i>
                        {t("Yenile")}
                    </Button>
                </CardHeader>
                <CardBody>
                    <DataGrid
                        dataSource={transferList}
                        keyExpr="order_id"
                        showBorders={true}
                        columnAutoWidth={true}
                        rowAlternationEnabled={true}
                    >
                        <FilterRow visible={true} />
                        <HeaderFilter visible={true} />
                        <Paging defaultPageSize={10} />
                        <Scrolling mode="standard" />

                        <Column dataField="order_id" caption={t("Emir No")} width={110} alignment="center" />
                        <Column dataField="created_at" caption={t("Tarih")} dataType="datetime" format="dd/MM/yyyy HH:mm" width={140} alignment="center" />
                        <Column dataField="from_warehouse_name" caption={t("Çıkış Sahası")} />
                        <Column dataField="to_warehouse_name" caption={t("Varış Antreposu")} />
                        <Column
                            caption={t("Araç")}
                            width={110}
                            alignment="center"
                            cellRender={(cell) => (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 font-weight-bold"
                                    onClick={() => openChassisModal(cell.data)}
                                >
                                    <Badge color="info" pill>{cell.data.total_chassis} {t("Araç")}</Badge>
                                </Button>
                            )}
                        />
                        <Column dataField="durum" caption={t("Durum")} width={100} alignment="center" />

                        {/* T1 Document Column */}
                        <Column
                            caption={t("T1 Belgesi (Transit)")}
                            width={180}
                            alignment="center"
                            cellRender={(cell) => {
                                const t1 = cell.data.t1_number;
                                const path = cell.data.t1_file_path;
                                if (t1) {
                                    return (
                                        <div>
                                            <Badge color="primary" className="mb-1">{t1}</Badge>
                                            {path && (
                                                <div>
                                                    <a href={path} target="_blank" rel="noopener noreferrer" className="btn btn-outline-info btn-xs py-0 px-1">
                                                        <i className="fa fa-file-pdf-o mr-1"></i>{t("Dosya")}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return <Badge color="light" className="text-muted">{t("Eksik (Opsiyonel)")}</Badge>;
                            }}
                        />

                        {/* IM7 Document Column */}
                        <Column
                            caption={t("IM7 (Antrepo Giriş)")}
                            width={180}
                            alignment="center"
                            cellRender={(cell) => {
                                const im7 = cell.data.im7_number;
                                const path = cell.data.im7_file_path;
                                if (im7) {
                                    return (
                                        <div>
                                            <Badge color="warning" className="mb-1 text-dark">{im7}</Badge>
                                            {path && (
                                                <div>
                                                    <a href={path} target="_blank" rel="noopener noreferrer" className="btn btn-outline-warning btn-xs py-0 px-1">
                                                        <i className="fa fa-file-pdf-o mr-1"></i>{t("Dosya")}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return <Badge color="light" className="text-muted">{t("Eksik (Opsiyonel)")}</Badge>;
                            }}
                        />

                        {/* Actions Column */}
                        <Column
                            caption={t("İşlemler")}
                            width={150}
                            alignment="center"
                            cellRender={(cell) => (
                                <Button
                                    id={`btn-edit-docs-${cell.data.order_id}`}
                                    color="primary"
                                    size="sm"
                                    outline
                                    onClick={() => openDocModal(cell.data)}
                                >
                                    <i className="fa fa-edit mr-1"></i>{t("Evrak Yönetimi")}
                                </Button>
                            )}
                        />
                    </DataGrid>
                </CardBody>
            </Card>

            {/* Modal: Add / Update Customs Documents for an existing order */}
            <Modal isOpen={isDocModalOpen} toggle={() => setIsDocModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setIsDocModalOpen(false)}>
                    <i className="fa fa-file-text-o mr-2 text-primary"></i>
                    {t("Gümrük Evrakları Yönetimi")} — <Badge color="dark">{selectedOrder?.order_id}</Badge>
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted small mb-3">
                        <i className="fa fa-info-circle mr-1"></i>
                        {selectedOrder?.from_warehouse_name} → {selectedOrder?.to_warehouse_name} ({selectedOrder?.total_chassis} {t("araç")}) {t("transferine ait T1 ve IM7 evraklarını ekleyebilir veya güncelleyebilirsiniz.")}
                    </p>

                    <Row>
                        {/* T1 Section */}
                        <Col md="6" className="border-right">
                            <h6 className="font-weight-bold text-primary mb-2">
                                <i className="fa fa-shield mr-1"></i>{t("T1 Dahili Transit Belgesi")}
                            </h6>
                            <FormGroup>
                                <Label>{t("T1 Belge / MRN No")}</Label>
                                <Input
                                    id="modal-t1-number"
                                    type="text"
                                    placeholder="Örn: 26TR34000012345"
                                    value={editT1Number}
                                    onChange={(e) => setEditT1Number(e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t("T1 Belge Tarihi")}</Label>
                                <Input
                                    id="modal-t1-date"
                                    type="date"
                                    value={editT1Date}
                                    onChange={(e) => setEditT1Date(e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t("Yeni T1 Dosyası Yükle")}</Label>
                                <Input
                                    id="modal-t1-file"
                                    type="file"
                                    accept=".pdf, .png, .jpg, .jpeg"
                                    onChange={(e) => setEditT1File(e.target.files[0] || null)}
                                />
                                {selectedOrder?.t1_file_name && !editT1File && (
                                    <small className="text-muted d-block mt-1">
                                        {t("Mevcut dosya:")} <a href={selectedOrder.t1_file_path} target="_blank" rel="noopener noreferrer">{selectedOrder.t1_file_name}</a>
                                    </small>
                                )}
                            </FormGroup>
                        </Col>

                        {/* IM7 Section */}
                        <Col md="6">
                            <h6 className="font-weight-bold text-warning text-dark mb-2">
                                <i className="fa fa-building mr-1"></i>{t("IM7 Antrepo Beyannamesi")}
                            </h6>
                            <FormGroup>
                                <Label>{t("IM7 Beyanname No")}</Label>
                                <Input
                                    id="modal-im7-number"
                                    type="text"
                                    placeholder="Örn: 26340700IM000456"
                                    value={editIm7Number}
                                    onChange={(e) => setEditIm7Number(e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t("IM7 Tescil Tarihi")}</Label>
                                <Input
                                    id="modal-im7-date"
                                    type="date"
                                    value={editIm7Date}
                                    onChange={(e) => setEditIm7Date(e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>{t("Yeni IM7 Dosyası Yükle")}</Label>
                                <Input
                                    id="modal-im7-file"
                                    type="file"
                                    accept=".pdf, .png, .jpg, .jpeg"
                                    onChange={(e) => setEditIm7File(e.target.files[0] || null)}
                                />
                                {selectedOrder?.im7_file_name && !editIm7File && (
                                    <small className="text-muted d-block mt-1">
                                        {t("Mevcut dosya:")} <a href={selectedOrder.im7_file_path} target="_blank" rel="noopener noreferrer">{selectedOrder.im7_file_name}</a>
                                    </small>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup className="mt-2">
                        <Label>{t("Notlar")}</Label>
                        <Input
                            id="modal-notes"
                            type="textarea"
                            rows="2"
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setIsDocModalOpen(false)}>{t("Vazgeç")}</Button>
                    <Button
                        id="modal-save-docs-btn"
                        color="primary"
                        disabled={isSavingDocs}
                        onClick={handleSaveDocumentsModal}
                    >
                        <i className={`fa ${isSavingDocs ? 'fa-spinner fa-spin' : 'fa-save'} mr-1`}></i>
                        {t("Evrakları Kaydet")}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Modal: View Chassis List */}
            <Modal isOpen={isChassisModalOpen} toggle={() => setIsChassisModalOpen(false)}>
                <ModalHeader toggle={() => setIsChassisModalOpen(false)}>
                    <i className="fa fa-car mr-2"></i>{selectedOrder?.order_id} — {t("Taşınan Araçlar")} ({activeChassisList.length})
                </ModalHeader>
                <ModalBody style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table striped size="sm">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t("Şase No")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeChassisList.map((chassis, idx) => (
                                <tr key={chassis}>
                                    <td>{idx + 1}</td>
                                    <td><code>{chassis}</code></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setIsChassisModalOpen(false)}>{t("Kapat")}</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default AntrepoTransferEmri;
